<template>
  <JsonLDModel :jsonOptions="jsonOptions" />    
  <NuxtLayout>
    <div class="slot-body">
      <h1 v-if="dadosSSR?.nameModel" class="ssr-model-title">
        {{ (route.params.language === 'pt-br' ? 'Fotos de ' : 'Photos of ') + dadosSSR.nameModel }}
      </h1>
      <NavBarHeader
        v-if="dataModel"
        :model-info="dadosSSR"
        :model-data="dataModel"
      >
        <!-- :key remonta só quando a resposta do cliente muda a 1ª seção,
             que o Photos copia para refs no setup -->
        <Photos
          :key="versaoFotos"
          :loadindSpinner="false"
          :model="dadosSSR"
          :photosModel="dataModel"
          :aguardandoVersao="aguardandoVersao"
          @showModalSubscribePlans="showModalSubscribePlans"
        />
        <div id="profileModel" class="block block--gray mt-1">
          <PerfilDescription
            v-if="dataModelPerfil?.profileQuestion"
            :model="dadosSSR"
            :perfilModel="dataModelPerfil"
            :divStyle="divStyle"
          />
          <div
            v-if="buttonShow"
            class="row d-flex align-items-center justify-content-center"
            style="margin-left:0px;margin-right:0px;"
          >
            <button
              type="button"
              class="more-profile mb-5 p-3 col-lg-3 col-8 z-1"
              @click="viewProfile"
            >
              {{ translate.viewProfile }}
              <font-awesome :icon="['fas', 'arrow-down']" size="md" />
            </button>
          </div>
          <ClientOnly>
            <BannerAssineHorizontal :path="bannerAssineHorizontal" />
          </ClientOnly>
        </div>
      </NavBarHeader>
      <ModalSubscribePlans
        @showModalSubscribePlans="showModalSubscribePlans"
        :showSubscribe="showSubscribe"
        :plans="storePlan.plans"
        v-if="storePlan.plans"
      />
    </div>
  </NuxtLayout>
</template>
<script lang="ts" setup>
// SEO: página de conteúdo explícito — marcação que o Google pede para não
// classificar o site inteiro como adulto. Ver composables/useAdultRating.ts
useAdultRating();
import Photos from "@/components/Model/Photos.vue";
import PerfilDescription from "@/components/Model/PerfilDescription.vue";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import ModalSubscribePlans from "@/components/Modais/ModalSubscribePlansAndCredits.vue";
import NavBarHeader from "@/components/Model/NavBarHeader.vue";
import api from "@/api";
import { isMobile } from "@/shared/utils/mobile";
import translateModel from "@/tradutors/model";
import JsonLDModel from "@/components/JsonLDModel.vue";
import {
  completarMiniaturas,
  publicPerfilPayload,
  publicPhotosPayload,
} from "@/utils/publicModelPayload";
import { respostaFotosValida, trocaSemRemontar } from "@/utils/ensaioSSR";

definePageMeta({
  middleware: ["not-found-lang"],
});

const route = useRoute();
const storeUser = userStore();
const storePlan = planStore();

const translate = computed(() =>
  route.params.language == "pt-br"
    ? translateModel.portugues
    : translateModel.ingles
);

const ensaio = useEnsaioRota();
const urlFotos = `v2/modelos/${ensaio.nome}/photos?${ensaio.query}`;
const urlPerfil = `v2/modelos/${ensaio.nome}/perfilWallpaperCredits?${ensaio.query}`;

const [metadados, fotosSSR, perfilSSR] = await Promise.all([
  useEnsaioMetadados(),
  useEnsaioPublico(
    "model-photos-public",
    `v2/modelos/${ensaio.nome}/photos`,
    publicPhotosPayload
  ),
  useEnsaioPublico(
    "model-perfil-public",
    `v2/modelos/${ensaio.nome}/perfilWallpaperCredits`,
    publicPerfilPayload
  ),
]);
exigirMetadados(metadados.error.value, metadados.data.value);

const dadosSSR = ref(metadados.data.value);
const canonical = useModelCanonical(route.params.name, route.params.language);
useHead({
  templateParams: {
    subTitle: null,
    subPage: dadosSSR.value.seoTitleModel ?? "",
    page_url: canonical,
    page_description:
      dadosSSR.value.seoDescriptionModel ?? dadosSSR.value.descriptionModel,
    page_image: shareImage(dadosSSR.value.imgPerfil),
    page_keywords:
      dadosSSR.value.meta_keywords ??
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site, bella blog",
    page_locale: route.params.language as string,
    page_url_pt:
      "https://belladasemana.com.br/pt-br/modelos/" +
      route.params.name +
      "/photo",
    page_url_en:
      "https://belladasemana.com.br/en/modelos/" + route.params.name + "/photo",
    page_canonical: canonical,
  },
});

const jsonOptions = ref({
  type: "Person",
  name: dadosSSR.value.nameModel || dadosSSR.value.seoTitleModel || "",
  jobTitle: 'Model',
  image: dadosSSR.value.imgPerfil ?? "",
  url: canonical
});

const bannerAssineHorizontal = ref("");
const showSubscribe = ref(false);
const divStyle = ref("div-profile-hidden");
const buttonShow = ref(true);

// O SSR traz a versão pública (anônima); a do cliente, quando vem, prevalece.
const fotosPublicas = ref(fotosSSR.data.value);
const fotosCliente = ref<any>(null);
const dataModel = computed(() => fotosCliente.value ?? fotosPublicas.value);
const versaoFotos = ref(0);
// Logado, a versão do SSR (anônima) pode não ser a do usuário: até a dele
// chegar, as seções ficam em caixas neutras, sem cadeado. Liga só depois de
// montar, então o HTML e a hidratação não mudam.
const aguardandoVersao = ref(false);

const perfilCliente = ref<any>(null);
const dataModelPerfil = computed(() => perfilCliente.value ?? perfilSSR.data.value);

onMounted(() => {
  getBannerPath();
  carregarFotos();
  if (!perfilSSR.data.value) carregarPerfil();
  divStyle.value = "div-profile-hidden";
  buttonShow.value = true;
});

async function carregarFotos() {
  const publicas = fotosPublicas.value;
  // Logado, a resposta pode mudar (seções liberadas, zoom, álbuns): busca
  // completa. Anônimo com SSR só completa as miniaturas que o orçamento cortou.
  if (publicas && !jwtStore().jwt) {
    if (publicas.incompleto) quandoOcioso(completarMiniaturasDoSSR);
    return;
  }
  aguardandoVersao.value = !!publicas;
  try {
    const completo = await buscarEnsaioNoCliente(urlFotos);
    if (!respostaFotosValida(completo)) return;
    // Sem remontar, o carrossel continua na foto em que o usuário está.
    if (!trocaSemRemontar(dataModel.value, completo)) versaoFotos.value++;
    fotosCliente.value = completo;
  } finally {
    // Se a busca falhar, fica a versão pública, com os cadeados.
    aguardandoVersao.value = false;
  }
}

async function completarMiniaturasDoSSR() {
  const completo = await buscarEnsaioNoCliente(urlFotos);
  if (!respostaFotosValida(completo) || !fotosPublicas.value || fotosCliente.value) return;
  fotosPublicas.value = completarMiniaturas(fotosPublicas.value, completo);
}

async function carregarPerfil() {
  const perfil = await buscarEnsaioNoCliente(urlPerfil);
  if (perfil && typeof perfil === "object" && !("message" in perfil)) {
    perfilCliente.value = perfil;
  }
}

function quandoOcioso(fn: () => void) {
  if ("requestIdleCallback" in window) window.requestIdleCallback(fn);
  else setTimeout(fn, 1);
}

function viewProfile() {
  divStyle.value = "div-profile-show";
  buttonShow.value = false;
}

function getBannerPath() {
  if (!sessionStorage.getItem("bannerAssineHorizontal"))
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
          route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}
function showModalSubscribePlans(value: boolean) {
  if (!storeUser.isActiveUser) {
    showSubscribe.value = value;
    if (value == true)
      showModalPlans(
        "Clicar para reproduzir o video sem ter uma assinatura ativa"
      );
  }
}
function showModalPlans(acionador: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "paywall",
    paywall: { acionador: acionador },
  });
}
</script>
<style scoped lang="css">
.tab-panels :deep(.tab-panel) {
  width: 98%;
}
.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
}

.title {
  font-family: "Bebas-Neue-Light";
  font-size: 60px;
  font-weight: 500;
  color: black;
}

.title-models {
  font-family: "Exo-2";
  font-size: 32px;
  font-weight: 600;
  color: #b20000;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
}
.more-profile {
  font-weight: 500;
  background-color: black !important;
  border: 1px solid black !important;
  border-radius: 30px !important;
  color: white;
  font-family: "Exo-2";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
}

.more-profile svg {
  color: #b20;
  margin-left: 8px;
}

.ssr-model-title {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
}
</style>
